import React, { useState, useEffect } from 'react';
import { Search, Edit2, Trash2, Plus } from 'lucide-react';
import { getEnvVar } from '../../../config/env';
import { WorkspacePricingModal } from './WorkspacePricingModal';

interface WorkspacePricing {
  workspace_id: string;
  workspace_name: string;
  tier_name: string;
  tier_description?: string;
  whatsapp_cost: number;
  voice_cost: number;
  ai_task_cost: number;
  email_cost: number;
  sms_cost: number;
  bulk_threshold: number;
  bulk_discount: number;
  auto_topup_enabled?: boolean;
  monthly_credit_limit?: number;
}

interface WorkspaceOverride {
  id: string;
  workspace_id: string;
  workspaces?: { name: string };
  pricing_tier_id?: string;
  credit_pricing_tiers?: { tier_name: string; tier_description?: string };
  whatsapp_cost_override?: number;
  voice_cost_override?: number;
  ai_task_cost_override?: number;
  email_cost_override?: number;
  sms_cost_override?: number;
  bulk_discount_threshold_override?: number;
  bulk_discount_percentage_override?: number;
  auto_topup_enabled?: boolean;
  monthly_credit_limit?: number;
  is_active: boolean;
}

export const WorkspacePricingTab: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<WorkspacePricing[]>([]);
  const [overrides, setOverrides] = useState<WorkspaceOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOverride, setEditingOverride] = useState<WorkspaceOverride | null>(null);

  const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

  useEffect(() => {
    loadWorkspacePricing();
  }, []);

  const loadWorkspacePricing = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('admin_auth_token');
      
      // Load pricing overrides
      const overridesResponse = await fetch(`${API_BASE_URL}/api/v1/admin/pricing/workspaces?active_only=false`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!overridesResponse.ok) {
        const errorText = await overridesResponse.text();
        throw new Error(`Failed to load workspace pricing: ${errorText}`);
      }

      const overridesResult = await overridesResponse.json();
      
      if (overridesResult.success) {
        const overridesData = overridesResult.data || [];
        
        // Transform override data to match WorkspacePricing interface
        const pricingData = overridesData.map((override: WorkspaceOverride) => ({
          workspace_id: override.workspace_id,
          workspace_name: override.workspaces?.name || 'Unknown',
          tier_name: override.credit_pricing_tiers?.tier_name || 'professional',
          tier_description: override.credit_pricing_tiers?.tier_description,
          whatsapp_cost: override.whatsapp_cost_override || 0,
          voice_cost: override.voice_cost_override || 0,
          ai_task_cost: override.ai_task_cost_override || 0,
          email_cost: override.email_cost_override || 0,
          sms_cost: override.sms_cost_override || 0,
          bulk_threshold: override.bulk_discount_threshold_override || 0,
          bulk_discount: override.bulk_discount_percentage_override || 0,
          auto_topup_enabled: override.auto_topup_enabled,
          monthly_credit_limit: override.monthly_credit_limit,
        }));
        
        setOverrides(overridesData);
        setWorkspaces(pricingData);
      } else {
        throw new Error(overridesResult.error || 'Failed to load workspace pricing');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workspace pricing');
      console.error('Error loading workspace pricing:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingOverride(null);
    setShowModal(true);
  };

  const handleEdit = (override: WorkspaceOverride) => {
    setEditingOverride(override);
    setShowModal(true);
  };

  const handleDelete = async (workspaceId: string) => {
    if (!confirm('Are you sure you want to deactivate this workspace pricing override?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/pricing/workspaces/${workspaceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete workspace pricing override');
      }

      await loadWorkspacePricing();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete workspace pricing override');
      console.error('Error deleting workspace pricing override:', err);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingOverride(null);
  };

  const handleModalSave = () => {
    loadWorkspacePricing();
    handleModalClose();
  };

  const filteredWorkspaces = workspaces.filter((ws) =>
    ws.workspace_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ws.tier_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header Actions */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Workspace Pricing</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage workspace-specific pricing overrides
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Override
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search workspaces..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Workspaces Table */}
      {filteredWorkspaces.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">
            {searchTerm ? 'No workspaces found matching your search.' : 'No workspace pricing overrides found.'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Workspace
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  WhatsApp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Voice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SMS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredWorkspaces.map((ws) => {
                const override = overrides.find((o) => o.workspace_id === ws.workspace_id);
                return (
                  <tr key={ws.workspace_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{ws.workspace_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full capitalize">
                        {ws.tier_name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{ws.whatsapp_cost.toFixed(2)}/msg
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{ws.voice_cost.toFixed(2)}/min
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{ws.email_cost.toFixed(2)}/email
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{ws.sms_cost.toFixed(2)}/SMS
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          override?.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {override?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {override && (
                          <>
                            <button
                              onClick={() => handleEdit(override)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(ws.workspace_id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <WorkspacePricingModal
          override={editingOverride}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};
